import Typo from "@/src/components/atom/Typo";
import Flex from "@/src/components/molecule/Flex";
import SectionCardWithoutHeader from "@/src/components/molecule/SectionCardWithoutHeader";
import colorSet, { ColorType } from "@/src/styles/color";
import React, { useState } from "react";
import styled from "styled-components";
import { ReactComponent as InfoSvg } from "@/src/assets/icons/icon-info-gray6.svg";
import { ReactComponent as CreditColorSvg } from "@/src/assets/icons/icon-credit-color.svg";
import { Button } from "@/src/components/atom/Button";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import { useTranslation } from "react-i18next";
import BuyCreditDialog from "./dialog/BuyCreditDialog";
import { CreditSelectTabState } from "..";
import { subscriptionApi } from "@/src/store/apis/subscription";
import { isUndefined } from "@/src/utils/is";
import { creditApi } from "@/src/store/apis/credit";

enum DialogState {
  NULL,
  BUY_CREDIT,
}

interface CreditInfoProps {
  onTabChange: (tab: CreditSelectTabState) => void;
}

function CreditInfo({ onTabChange }: CreditInfoProps) {
  const { t } = useTranslation();
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.NULL);

  // API
  const {
    totalAvailableCredit,
    totalSubscriptionCredit,
    totalAvailableSubscriptionCredit,
    totalAvailableOneTimePurchaseCredit,
  } = creditApi.endpoints.getCreditStatusInfo.useQueryState(undefined, {
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        totalAvailableCredit: isStable
          ? currentData.totalAvailableCredit.toLocaleString("ko-KR")
          : "0",
        totalSubscriptionCredit: isStable
          ? currentData.totalSubscriptionCredit.toLocaleString("ko-KR")
          : "0",
        totalAvailableSubscriptionCredit: isStable
          ? currentData.totalAvailableSubscriptionCredit.toLocaleString("ko-KR")
          : "0",
        totalAvailableOneTimePurchaseCredit: isStable
          ? currentData.totalAvailableOneTimePurchaseCredit.toLocaleString(
              "ko-KR"
            )
          : "0",
      };
    },
  });

  const { isPaymentFetch, isNoCard } =
    subscriptionApi.endpoints.getPaymentMethods.useQueryState(
      {
        page: 1,
        pageSize: 2,
      },
      {
        selectFromResult: ({ currentData, isError, isFetching }) => {
          const isUnstable = isError || isFetching || isUndefined(currentData);
          const isStable = !isUnstable;

          return {
            isPaymentFetch: isFetching,
            isNoCard: isStable ? currentData.rows.length === 0 : true,
          };
        },
      }
    );

  const { isSubscriptionProgress } =
    subscriptionApi.endpoints.getCurrentSubscription.useQueryState(undefined, {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isSubscriptionProgress: isStable
            ? currentData?.subscriptionStatus === "PROGRESS"
            : false,
        };
      },
    });

  const creditInfoList = [
    {
      title: t("common:monthlyCredit"),
      content: (
        <Flex gap={4}>
          <BreakWordTypo typoType="h4" color="blue4">
            {totalAvailableSubscriptionCredit}
          </BreakWordTypo>
          <BreakWordTypo typoType="h4">
            / {totalSubscriptionCredit}
          </BreakWordTypo>
        </Flex>
      ),
      notice: t("common:monthlyExpireNotice"),
    },
    {
      divider: true,
    },
    {
      title: t("common:purchasedCredit"),
      content: (
        <BreakWordTypo typoType="h4">
          {totalAvailableOneTimePurchaseCredit}
        </BreakWordTypo>
      ),
      notice: t("common:purchasedValidityNotice"),
      rightAccessory: (
        <Button
          buttonGrade="secondary"
          buttonSize={32}
          onClick={() => setDialogState(DialogState.BUY_CREDIT)}
          isLoading={isPaymentFetch}
          disabled={isPaymentFetch || isNoCard || !isSubscriptionProgress}
        >
          {t("common:buyCredit")}
        </Button>
      ),
    },
  ];

  const renderDialog = () => {
    switch (dialogState) {
      case DialogState.BUY_CREDIT:
        return (
          <BuyCreditDialog
            open={dialogState === DialogState.BUY_CREDIT}
            onOpenChange={() => setDialogState(DialogState.NULL)}
            onTabChange={onTabChange}
          />
        );
    }
  };

  return (
    <Flex gap={16}>
      <StyledSectionCardWithoutHeader>
        <CreditInfoContainer gap={19} alignItems="center">
          <CreditIcon />

          <Flex flexDirection="column" gap={4}>
            <Typo typoType="b6m">{t("common:availableCredit")}</Typo>
            <Flex gap={8} alignItems="end">
              <BreakWordTypo typoType="d3" color="blue4">
                {totalAvailableCredit}
              </BreakWordTypo>
              <Typo typoType="h2" color="blue4">
                {t("common:credit")}
              </Typo>
            </Flex>
          </Flex>
        </CreditInfoContainer>
      </StyledSectionCardWithoutHeader>

      <StyledSectionCardWithoutHeader>
        <Flex gap={32}>
          {creditInfoList.map(
            ({ title, content, notice, divider, rightAccessory }, idx) => {
              if (divider) {
                return <Divider key={idx} />;
              }

              return (
                <Flex flexDirection="column" gap={12} flex={1} key={idx}>
                  <Flex justifyContent="space-between" gap={16}>
                    <Flex flexDirection="column" gap={8}>
                      <Typo typoType="b6m">{title}</Typo>
                      {content}
                    </Flex>
                    <div>{rightAccessory}</div>
                  </Flex>

                  {/* Notice */}
                  <Flex gap={4}>
                    <InfoIcon color="gray6" />
                    <Typo typoType="b9r" color="gray6">
                      {notice}
                    </Typo>
                  </Flex>
                </Flex>
              );
            }
          )}
        </Flex>
      </StyledSectionCardWithoutHeader>
      {renderDialog()}
    </Flex>
  );
}

export default CreditInfo;

const StyledSectionCardWithoutHeader = styled(SectionCardWithoutHeader)`
  padding: 24px;
`;

const CreditInfoContainer = styled(Flex)`
  height: 100%;
`;

const Divider = styled.div`
  width: 1px;
  background-color: ${colorSet.gray9};
`;

const InfoIcon = styled(InfoSvg)<{ color: ColorType }>`
  position: relative;
  top: 1px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  path {
    fill: ${({ color }) => colorSet[color]};
  }
`;

const CreditIcon = styled(CreditColorSvg)`
  width: 50px;
  height: 50px;
  flex-shrink: 0;
`;
